import type { ComponentProps } from "react";

import { ModelSelectorLogo } from "@/components/ai-elements/model-selector";
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectSeparator,
	SelectTrigger,
} from "@/components/ui/select";

type AdvancedModelPickerValue =
	| "moonshot:kimi-k2.5"
	| "openrouter:openai/gpt-5.4-nano"
	| "openrouter:openai/gpt-5.4-mini"
	| "openrouter:openai/gpt-5.4"
	| "openrouter:anthropic/claude-opus-4-6"
	| "openrouter:moonshotai/kimi-k2.5"
	| "openrouter:z-ai/glm-5.1"
	| "openrouter:qwen/qwen3.6-plus:free"
	| "openrouter:deepseek/deepseek-v4-pro";

type AdvancedModelOption = {
	value: AdvancedModelPickerValue;
	label: string;
	group:
		| "Moonshot"
		| "OpenAI via OpenRouter"
		| "Anthropic via OpenRouter"
		| "Moonshot via OpenRouter"
		| "Z.AI via OpenRouter"
		| "Qwen via OpenRouter"
		| "DeepSeek via OpenRouter";
	logo: ComponentProps<typeof ModelSelectorLogo>["provider"];
	description: string;
};

const MODEL_OPTIONS: AdvancedModelOption[] = [
	{
		value: "moonshot:kimi-k2.5",
		label: "Kimi K2.5",
		group: "Moonshot",
		logo: "moonshotai",
		description: "moonshot:kimi-k2.5",
	},
	{
		value: "openrouter:openai/gpt-5.4-nano",
		label: "GPT-5.4 Nano",
		group: "OpenAI via OpenRouter",
		logo: "openrouter",
		description: "openrouter:openai/gpt-5.4-nano",
	},
	{
		value: "openrouter:openai/gpt-5.4-mini",
		label: "GPT-5.4 Mini",
		group: "OpenAI via OpenRouter",
		logo: "openrouter",
		description: "openrouter:openai/gpt-5.4-mini",
	},
	{
		value: "openrouter:openai/gpt-5.4",
		label: "GPT-5.4",
		group: "OpenAI via OpenRouter",
		logo: "openrouter",
		description: "openrouter:openai/gpt-5.4",
	},
	{
		value: "openrouter:anthropic/claude-opus-4-6",
		label: "Claude Opus 4.6",
		group: "Anthropic via OpenRouter",
		logo: "anthropic",
		description: "openrouter:anthropic/claude-opus-4-6",
	},
	{
		value: "openrouter:moonshotai/kimi-k2.5",
		label: "Kimi K2.5",
		group: "Moonshot via OpenRouter",
		logo: "moonshotai",
		description: "openrouter:moonshotai/kimi-k2.5",
	},
	{
		value: "openrouter:z-ai/glm-5.1",
		label: "GLM 5.1",
		group: "Z.AI via OpenRouter",
		logo: "z-ai",
		description: "openrouter:z-ai/glm-5.1",
	},
	{
		value: "openrouter:qwen/qwen3.6-plus:free",
		label: "Qwen 3.6 Plus",
		group: "Qwen via OpenRouter",
		logo: "qwen",
		description: "openrouter:qwen/qwen3.6-plus",
	},
	{
		value: "openrouter:deepseek/deepseek-v4-pro",
		label: "DeepSeek V4 Pro",
		group: "DeepSeek via OpenRouter",
		logo: "deepseek",
		description: "openrouter:deepseek/deepseek-v4-pro",
	},
];

const GROUP_ORDER = [
	"Moonshot",
	"OpenAI via OpenRouter",
	"Anthropic via OpenRouter",
	"Moonshot via OpenRouter",
	"Z.AI via OpenRouter",
	"Qwen via OpenRouter",
	"DeepSeek via OpenRouter",
] as const satisfies AdvancedModelOption["group"][];

export type AdvancedModelPickerProps = {
	value: string;
	onValueChange: (value: string) => void;
};

export function AdvancedModelPicker({ value, onValueChange }: AdvancedModelPickerProps) {
	const selectedOption =
		MODEL_OPTIONS.find((option) => option.value === value) ?? MODEL_OPTIONS[0];
	const visibleGroups = GROUP_ORDER.map((group) => ({
		group,
		options: MODEL_OPTIONS.filter((option) => option.group === group),
	})).filter((entry) => entry.options.length > 0);

	return (
		<Select
			onValueChange={(nextValue) => {
				if (nextValue) {
					onValueChange(nextValue);
				}
			}}
			value={selectedOption.value}
		>
			<SelectTrigger size="sm" className="max-w-[calc(100vw-4rem)]">
				<span className="flex min-w-0 items-center gap-2">
					<ModelSelectorLogo
						provider={selectedOption.logo}
						className="size-4 dark:invert-0"
					/>
					<span className="truncate">{selectedOption.label}</span>
				</span>
			</SelectTrigger>
			<SelectContent align="start" className="w-[min(90vw,360px)] min-w-[320px]">
				{visibleGroups.map(({ group, options: groupOptions }, index) => (
					<div key={group}>
						{index > 0 && <SelectSeparator />}
						<SelectGroup>
							<SelectLabel>{group}</SelectLabel>
							{groupOptions.map((option) => (
								<SelectItem key={option.value} value={option.value}>
									<div className="flex min-w-0 items-center gap-2">
										<ModelSelectorLogo
											provider={option.logo}
											className="size-4 dark:invert-0"
										/>
										<div className="flex min-w-0 flex-col">
											<span className="truncate">{option.label}</span>
											<span className="text-[11px] leading-tight text-muted-foreground break-all">
												{option.description}
											</span>
										</div>
									</div>
								</SelectItem>
							))}
						</SelectGroup>
					</div>
				))}
			</SelectContent>
		</Select>
	);
}
